export const SKILLS_RESOLVE_VERSION = 1 as const;
export const MAX_SELECTED_SKILLS = 3;

export type SkillScope = "personal" | "team" | "org";
export type SkillSource = "manual" | "directory";
export type SkillOrigin = "personal" | "organization" | "finster";
export type SkillDensity = "default" | "compact";
export type SkillSourceFilterValue = "all" | "personal" | "system";
export type SkillViewStatus = "idle" | "loading" | "ready" | "error";

export type SkillDataSourceSelection =
  | { mode: "all" }
  | { mode: "custom"; dataSourceIds: string[] };

/** Display metadata returned by the personal and directory list endpoints. */
export interface SkillSummary {
  id: string;
  name: string;
  slug: string;
  description: string;
  enabled: boolean;
  scope: SkillScope;
  source: SkillSource;
  /** Display-only provenance added when personal and directory lists merge. */
  origin?: SkillOrigin;
  category?: string | null;
  updatedAt?: string;
  companyId?: string | null;
  companyName?: string | null;
}

/** Fully hydrated record returned by either detail endpoint. */
export interface Skill extends SkillSummary {
  instructions: string;
  dataSources: SkillDataSourceSelection;
  targetFileIds: string[];
  userFileIds?: string[];
  createdAt: string;
  updatedAt: string;
  userId?: string;
  orgId?: string | null;
  sourceSkillId?: string;
}

export interface SkillListResponse {
  items: SkillSummary[];
}

export interface SkillsCatalog {
  personal: SkillSummary[];
  directory: SkillSummary[];
}

export interface SelectedSkillReference {
  id: string;
  name: string;
  slug: string;
}

/** Short aliases used by the shared UI contracts. */
export type SelectedSkillRef = SelectedSkillReference;
export type SkillDetail = Skill;

export interface GroupedSkills<T extends SkillSummary = SkillSummary> {
  personal: T[];
  organization: T[];
  finster: T[];
}

export type SkillsResolveMessage = Record<string, unknown>;

export interface SkillsResolveExplorerContextRequest {
  user_file_ids?: string[] | null;
  user_file_tags?: string[] | null;
}

/**
 * Exact request DTO accepted by data-feed's stateless version-1 resolver.
 * Omitting `skill_ids` requests automatic selection; providing it, including
 * `[]`, is an explicit selection.
 */
export interface SkillsResolveRequest {
  submission_id: string;
  messages: SkillsResolveMessage[];
  skill_ids?: string[];
  data_sources?: string[];
  explorer_context?: SkillsResolveExplorerContextRequest;
}

export type SkillsResolveSource = "explicit" | "inferred" | "none";

export interface ResolvedSkillDisplay {
  id: string;
  name: string;
  slug: string;
  origin: SkillOrigin;
  description: string;
  data_source_mode: "all" | "custom";
  data_source_ids: string[];
  target_file_ids: string[];
}

export interface ResolvedSkillTargetFile {
  id: string;
  name: string;
  file_type: string;
  status: string;
}

export interface SkillsResolveExplorerContext {
  user_file_ids: string[] | null;
  user_file_tags: string[] | null;
  skill_target_files: ResolvedSkillTargetFile[];
}

export interface SkillsResolveSelectorDiagnostics {
  path: string;
  matched: boolean;
  fallback: boolean;
  latency_ms: number;
  source: string | null;
}

/** Exact version-1 response DTO exposed by data-feed PR #4388. */
export interface SkillsResolveResponse {
  version: typeof SKILLS_RESOLVE_VERSION;
  skill_policy_version: number;
  submission_id: string;
  source: SkillsResolveSource;
  skill_ids: string[];
  skills: ResolvedSkillDisplay[];
  guidance: string | null;
  guidance_chars: number;
  targets_stripped: boolean;
  effective_data_sources: string[] | null;
  effective_explorer_context: SkillsResolveExplorerContext;
  selector: SkillsResolveSelectorDiagnostics;
}

export type SkillsResolveMode = "automatic" | "explicit";
